import React from 'react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiActivity, FiSmile, FiPhone, FiMapPin, FiClock, FiAward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const HeroSection = ({
  title,
  subtitle,
  description,
  image,
  imageAlt = "Hero Image",
  badge,
  showTrustIndicators = true,
  primaryButton = { text: "Book Consultation", href: "tel:+919581556660", icon: FiPhone },
  secondaryButton = { text: "Find Center", href: "/contact" },
  backgroundClass = "bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/50",
  trustIndicators = [
    { value: "15+", label: "Years Experience" },
    { value: "30K+", label: "Happy Patients" },
    { value: "100%", label: "Safe & Natural" }
  ]
}) => {
  return (
    <section className={`relative pt-24 pb-16 lg:pt-8 lg:pb-20 ${backgroundClass} overflow-hidden`}>
      {/* Subtle background elements */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      {/* Floating accent elements */}
      <div className="absolute top-16 right-16 w-24 h-24 bg-emerald-200/20 rounded-full blur-xl"></div>
      <div className="absolute bottom-16 left-16 w-32 h-32 bg-teal-200/20 rounded-full blur-xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center lg:text-left"
          >
            {/* Badge */}
            {badge && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100/80 text-emerald-700 text-sm font-medium mb-6 backdrop-blur-sm border border-emerald-200/50"
              >
                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                {badge}
              </motion.div>
            )}

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl  font-black text-gray-900 mb-6 leading-tight"
            >
              {title}
            </motion.h1>

            {/* Subtitle */}
            {subtitle && (
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.35 }}
                className="text-xl sm:text-2xl text-emerald-700 font-semibold mb-4"
              >
                {subtitle}
              </motion.h2>
            )}

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl"
            >
              {description}
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <a
                href={primaryButton.href}
                className="inline-flex items-center justify-center px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 group"
              >
                {primaryButton.icon && <SafeIcon icon={primaryButton.icon} className="w-5 h-5 mr-2" />}
                <span>{primaryButton.text}</span>
                <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href={secondaryButton.href}
                className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-emerald-600 font-semibold rounded-xl border-2 border-emerald-200 hover:border-emerald-300 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <span>{secondaryButton.text}</span>
              </a>
            </motion.div>

            {/* Trust Indicators */}
            {showTrustIndicators && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-wrap justify-center lg:justify-start gap-6 mt-8 pt-6 border-t border-gray-200/50"
              >
                {trustIndicators.map((indicator, index) => (
                  <div key={index} className="text-center">
                    <div className="text-lg font-bold text-emerald-600">{indicator.value}</div>
                    <div className="text-xs text-gray-500 font-medium">{indicator.label}</div>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative lg:order-last order-first"
          >
            <div className="relative rounded-[2rem] overflow-hidden bg-white shadow-2xl border border-emerald-100">
              <img
                src={image}
                alt={imageAlt}
                className="w-full h-80 sm:h-96 lg:h-[500px] object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=600&fit=crop';
                }}
              />
              {/* Subtle overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/10 via-transparent to-transparent"></div>

              {/* Floating badge - Enhanced */}
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-md rounded-full px-4 py-2 text-xs font-bold text-emerald-700 border border-emerald-200 shadow-lg">
                ✓ Trusted Care
              </div>
            </div>

            {/* Decorative elements - Enhanced */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-emerald-100/60 rounded-full blur-2xl"></div>
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-teal-100/60 rounded-full blur-2xl"></div>
          </motion.div>
        </div>
      </div>

      {/* Bottom decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400"></div>
    </section>
  );
};

export default HeroSection;